import {useEffect, useState} from "react";
import {ChecklistOptions} from "modules/projects/actions/checklist_options_fetch";

export interface ChecklistSelectorState {
  options?: ChecklistOptions;
  value: number | null;
  onChange: (issuesetId: number | null) => void;
  isLocked: boolean;
  hasLoadFailed: boolean;
}

interface ChecklistSelectionArgs {
  organisationId: number;
  projectId: number;
  isEnabled: boolean;
  projectChecklistIds?: number[] | null;
  familyChecklistId: number | null;
  locksFamilyChecklist?: boolean;
  fetchChecklistOptions: (
    organisationId: number,
    projectId: number,
  ) => Promise<{value: ChecklistOptions}>;
}

interface ChecklistSelection {
  checklistSelector: ChecklistSelectorState | null;
  checklistIssuesets: number[] | null;
}

export default function useChecklistSelection({
  organisationId,
  projectId,
  isEnabled,
  projectChecklistIds,
  familyChecklistId,
  locksFamilyChecklist = true,
  fetchChecklistOptions,
}: ChecklistSelectionArgs): ChecklistSelection {
  const knownOnlyId =
    projectChecklistIds?.length === 1 ? Number(projectChecklistIds[0]) : null;
  const needsOptions = isEnabled && knownOnlyId === null;
  const [options, setOptions] = useState<ChecklistOptions | undefined>();
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    setSelectedId(null);
    setOptions(undefined);
    setHasLoadFailed(false);
    if (!needsOptions) {
      return undefined;
    }
    let isCurrent = true;
    fetchChecklistOptions(organisationId, projectId)
      .then(({value}) => {
        if (isCurrent) {
          setOptions(value);
        }
      })
      .catch(() => {
        if (isCurrent) {
          setHasLoadFailed(true);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [organisationId, projectId, needsOptions]);

  useEffect(() => {
    if (!locksFamilyChecklist && familyChecklistId) {
      setSelectedId(familyChecklistId);
    }
  }, [familyChecklistId, locksFamilyChecklist]);

  const count = options?.checklists.length;
  const onlyId =
    knownOnlyId ?? (count === 1 ? options?.checklists[0].id ?? null : null);
  const lockedId = locksFamilyChecklist ? familyChecklistId : null;
  const checklistId = lockedId ?? onlyId ?? selectedId;
  const isShown = needsOptions && !(count !== undefined && count <= 1);

  return {
    checklistSelector: isShown
      ? {
          options,
          value: checklistId,
          onChange: setSelectedId,
          isLocked: Boolean(lockedId),
          hasLoadFailed,
        }
      : null,
    checklistIssuesets: isEnabled && checklistId ? [checklistId] : null,
  };
}
